/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  AwvInfo,
  ResultJobList,
  ResultJobLists,
  TasksApi,
  JobsApi,
} from "client";
import {
  useState,
  useRef,
  useCallback,
  FC,
  PropsWithChildren,
  SetStateAction,
  Dispatch,
  useEffect,
} from "react";
import { styled, alpha, lighten } from "@mui/material/styles";
import moment, { Moment } from "moment";
import {
  Calendar,
  EventProps,
  ResourceHeaderProps,
  momentLocalizer,
  View,
  DayLayoutAlgorithm,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { JobCard } from "./JobCard";
import { VehicleCard } from "./VehicleCard";
import { TimeGutterHeader } from "./TimeGutterHeader";
import { Box } from "@mui/material";
import { AwvControl } from "./AwvControl";
import { Const } from "const";
import {
  TargetTask,
  TaskControlDialog,
  TaskControlMode,
  TaskControlResult,
} from "components/Tasks/TaskControlDialog";
import { MultipleDetailDialog } from "components/Tasks/MultipleDetailDialog";
import { TaskData, initializeTaskData } from "context/TaskDataProvider";
import { TimeoutError } from "exception/TimeoutError";
import { ErrorDialog } from "components/Common/ErrorDialog";
import { useTranslation } from "react-i18next";
import { useCancelTask } from "hooks/CancelTaskHook";
import { TaskCompleteAlert } from "components/Common/TaskCompleteAlert";
import {
  getErrorResponse,
  getExtendCancelErrMsgCode,
  shouldAddErrMsg,
} from "utils/Api";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { useMapDataContext } from "context/MapDataProvider";


const DragAndDropCalendar = withDragAndDrop<ResultJobList, AwvInfo>(Calendar);
const localizer = momentLocalizer(moment);

const calendarOptions = {
  // BigCalendar
  localizer: localizer,
  onNavigate: () => {
    // NOP
  },
  defaultView: "day" as View,
  titleAccessor: "taskIdDisp" as keyof ResultJobList,
  startAccessor: "planningArrivalDatetime" as keyof ResultJobList,
  endAccessor: (task: ResultJobList) => {
    // job period is minimum 1 hour.
    const st = moment(task.planningArrivalDatetime);
    const ed = moment(task.planningDepartureDatetime);
    const diffInMinutes = ed.diff(st, 'minutes');
    const endAccessor = diffInMinutes > 60 ? ed.toDate() : st.add(1, 'hours').toDate();
    return endAccessor;
  },
  resourceAccessor: "frameTypeAndNumber" as keyof ResultJobList,
  resourceIdAccessor: "frameTypeAndNumber" as keyof AwvInfo,
  resourceTitleAccessor: "nameId" as keyof AwvInfo,
  toolbar: false,
  step: 15,
  timeslots: 4,
  formats: {
    timeGutterFormat: "HH:mm",
    eventTimeRangeFormat: () => {
      // disabled
      return "";
    },
    eventTimeRangeStartFormat: () => {
      // disabled
      return "";
    },
    eventTimeRangeEndFormat: () => {
      // disabled
      return "";
    },
  },
  messages: {
    allDay: "",
  },
  dayLayoutAlgorithm: "no-overlap" as DayLayoutAlgorithm,
  resizable: false,
  showMultiDayTimes: true,
};

export interface MultipleProps {
  awvInfos: AwvInfo[];
  tasks?: ResultJobList[];
  date: Moment;
  setDate: Dispatch<SetStateAction<Moment>>;
  canChangeDate?: boolean;
}

export const Multiple: FC<MultipleProps> = ({
  awvInfos,
  tasks,
  date,
  setDate,
  canChangeDate = true,
}) => {
  const { t } = useTranslation();
  // const { roleName } = useUserContext();
  const dateRef = useRef(date);
  const tasksRef = useRef(tasks);
  const { mapData } = useMapDataContext();
  const cancelTargetRef = useRef<ResultJobList>();
  const [jobsApi] = useApiWithTimeout(
    JobsApi,
    Const.operationTimeout.cancelTask
  );
  const cancelTask = useCancelTask();
  const cancelTaskExecute = cancelTask.setTargetTask;
  const [errMsg, setErrMsg] = useState("");
  const [showCancelTaskError, setShowCancelTaskError] = useState(false);
  const [showDeleteTaskError, setShowDeleteTaskError] = useState(false);
  const [tasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.deleteTask
  );

  /**
   * Set error message and display cancel task error dialog.
   * @param {string} addMsg
   */
  const dispCancelTaskError = useCallback(
    (addMsg: string) => {
      setErrMsg(
        cancelTask.error instanceof TimeoutError
          ? t("AWVUI-MSG84")
          : t("AWVUI-MSG24") + "\n\n" + t(addMsg)
      );
      // Show the error dialog.
      setShowCancelTaskError(true);
    },
    [cancelTask.error, t]
  );

  const getAddMessage = useCallback((getTaskResult: ResultJobLists) => {
    return getExtendCancelErrMsgCode(
      getTaskResult?.taskInfos && getTaskResult.taskInfos.length > 0
        ? getTaskResult.taskInfos[0].taskStatus
        : undefined
    );
  }, []);

  useEffect(() => {
    if (!cancelTask.loading && cancelTask.error) {
      // Get error response as Error object.
      getErrorResponse(cancelTask.error).then((errorResponse) => {
        // Get error code.
        let errorCode: string = "";
        if (errorResponse?.errors) {
          errorCode = errorResponse?.errors[0];
        }
        // If the task status is "completed".
        if (shouldAddErrMsg(errorCode)) {
          // Call get task api with the jobet id that was the target of cancellation.
          jobsApi
            .getJob({
              frameTypeAndNumberList: [
                cancelTargetRef.current?.frameTypeAndNumber!,
              ],
              mapId: mapData.mapId,
              jobSetId: cancelTargetRef.current?.jobSetId,
              isApproval: false,
            })
            .then((response) => {
              dispCancelTaskError(getAddMessage(response));
            })
            .catch((reason) => {
              console.error("reason: %o", reason);
              setErrMsg(
                reason.name === "TimeoutError"
                  ? t("AWVUI-MSG84")
                  : t("AWVUI-MSG24")
              );
              // Show the error dialog.
              setShowCancelTaskError(true);
            });
          // If the task status is not "compleated".
        } else {
          // Get additional text.
          const addMsg = getExtendCancelErrMsgCode(undefined);
          // Display error dialog.
          dispCancelTaskError(addMsg);
        }
      });
    }
  }, [
    cancelTask.error,
    cancelTask.loading,
    cancelTargetRef.current?.jobSetId,
    cancelTargetRef.current?.frameTypeAndNumber,
    t,
    mapData.mapId,
    jobsApi,
    dispCancelTaskError,
    getAddMessage,
  ]);

  useEffect(() => {
    if (cancelTask.value) {
      setShowCompleteMsg({ isOpen: true, label: t("AWVUI-MSG23") });
      cancelTaskExecute(undefined);
      setDate(moment(dateRef.current));
    }
  }, [cancelTask.value, cancelTaskExecute, setDate, t]);
  const [showCompleteMsg, setShowCompleteMsg] = useState({
    isOpen: false,
    label: "",
  });

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  // create Awv info component.
  const resourceHeaderComponent: FC<ResourceHeaderProps<AwvInfo>> = ({
    resource,
  }) => {
    return (
      <Box key={`header-${resource.frameTypeAndNumber}`}>
        <VehicleCard awvInfo={resource} />
        <AwvControl
          awvInfo={resource}
          tasks={tasksRef.current?.filter(
            (task) => task.frameTypeAndNumber === resource.frameTypeAndNumber
          )}
          cancelExecute={(task) => {
            cancelTargetRef.current = task;
            // Object creation to force state change
            cancelTaskExecute({ ...task });
          }}
          deleteTaskExecute={(jobsetId) => {
            deleteTask(jobsetId)
          }}
        />
      </Box>
    );
  };
  // create date select component.
  const timeGutterHeaderComponent: FC = () => {
    const handleDate = canChangeDate
      ? (day: number) => {
          setDate((prevDate) => {
            const newDate = moment(prevDate).add(day, "day");
            dateRef.current = newDate;
            return newDate;
          });
        }
      : undefined;
    return (
      <TimeGutterHeader
        date={dateRef.current}
        onPrevClick={
          handleDate
            ? () => {
                handleDate(-1);
              }
            : undefined
        }
        onNextClick={
          handleDate
            ? () => {
                handleDate(1);
              }
            : undefined
        }
      />
    );
  };

  const [controlTarget, setControlTarget] = useState<TargetTask>();
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [selectedTask, setSelectedTask] =
    useState<ResultJobList>(initializeTaskData);

  const getTaskControlResultMessage = useCallback(
    (mode: TaskControlMode, task: ResultJobList) => {
      switch (mode) {
        case "register":
          return t("AWVUI-MSG03");
        case "goHome":
          return t("AWVUI-MSG25");
        case "approve":
          return t("AWVUI-MSG03");
        default:
          break;
      }
    },
    [t]
  );

  const showToast = useCallback(
    (task?: TaskData) => {
      if (task && controlTarget?.mode) {
        const completedMsg = getTaskControlResultMessage(
          controlTarget.mode,
          task
        );
        setShowCompleteMsg({
          isOpen: !!completedMsg,
          label: completedMsg ?? "",
        });
      }
    },
    [controlTarget?.mode, getTaskControlResultMessage]
  );

  const handleControlDialogClose = useCallback(
    (result: TaskControlResult) => {
      if (result.status !== "canceled") {
        if (result.status === "completed") {
          showToast(result.task);
        }
        // task reload.
        setDate(moment(dateRef.current));
      }
      setControlTarget(undefined);
    },
    [setDate, showToast]
  );
  const handleDetailDialogClose = useCallback(
    (isEdited: boolean) => {
      if (isEdited) {
        // task reload.
        setDate(moment(dateRef.current));
      }
      setIsOpenDetail(false);
    },
    [setDate]
  );
  const handleCompleteClose = useCallback(() => {
    setShowCompleteMsg((current) => {
      return { ...current, isOpen: false };
    });
  }, []);

  /**
   * delete task
   *
   * @param {string} jobsetId
   */
  async function deleteTask(jobsetId: string) {
    try {
      await tasksApi.deleteTask({
        taskKind: 3,
        jobSetId: jobsetId
      });
      // succeed
      setDate(moment(dateRef.current));
      setShowCompleteMsg({
        isOpen: true,
        label: t("AWVUI-MSG223"),
      });
    } catch (error: unknown) {
      const msgId = error instanceof TimeoutError ? "AWVUI-MSG121" : "AWVUI-MSG43";
      setErrMsg(t(msgId));
      setShowDeleteTaskError(true);
    }
  }

  return (
    <>
      <MultipleContainer>
        <DragAndDropCalendar
          // static options
          {...calendarOptions}
          // BigCalendar
          date={(date ?? moment()).toISOString()}
          events={tasks?.filter((event) => ![5, 7, 8].includes(event.nodeCategory!))}
          resources={awvInfos}
          onSelectEvent={(task, e) => {
            console.debug("task: %o", task);
              // Show Task detail
              setSelectedTask(task);
              setIsOpenDetail(true);
          }}
          scrollToTime={moment
            .max(
              moment().subtract(Const.timelineRange, "hour"),
              moment().startOf("day")
            )
            .toDate()}
          components={{
            event: EventComponent,
            resourceHeader: resourceHeaderComponent,
            timeGutterHeader: timeGutterHeaderComponent,
            timeSlotWrapper: TimeSlotWrapperComponent,
          }}
        />
        <MultipleDetailDialog
          isOpen={isOpenDetail}
          task={selectedTask}
          jobs={tasks ? tasks?.filter((event) => ![7, 8].includes(event.nodeCategory!)) : []}
          onClose={handleDetailDialogClose}
        />
        {controlTarget && (
          <TaskControlDialog
            isOpen={controlTarget !== undefined}
            target={controlTarget}
            onClose={handleControlDialogClose}
          />
        )}
      </MultipleContainer>
      <ErrorDialog
        open={showCancelTaskError}
        key="cancelTaskError"
        message={errMsg}
        positiveButtonText={t("AWVUI-MSG11")}
        onPositiveButtonClick={() => {
          setShowCancelTaskError(false);
        }}
      />
      <ErrorDialog
        open={showDeleteTaskError}
        key="deleteTaskError"
        message={errMsg}
        positiveButtonText={t("AWVUI-MSG11")}
        onPositiveButtonClick={() => {
          setShowDeleteTaskError(false);
        }}
      />
      <TaskCompleteAlert {...showCompleteMsg} onClose={handleCompleteClose} />
    </>
  );
};

const EventComponent: FC<EventProps<ResultJobList>> = ({ event }) => {
  return <JobCard {...event} />;
};

const TimeSlotWrapperComponent: FC<PropsWithChildren> = ({ children }) => {
  return <Box sx={{ height: "2.4vh" }}>{children}</Box>;
};

const MultipleContainer = styled("div")(({ theme }) => ({
  flexGrow: 1,
  overflow: "hidden",
  ".rbc-calendar": {
    width: "100%",
    height: "100%",
    ".rbc-time-view.rbc-time-view-resources": {
      // Above header
      ".rbc-time-header": {
        borderRightColor: alpha(theme.palette.common.black, 0.3),
        // Timeline header
        ".rbc-time-header-gutter": {
          padding: 0,
          borderRightColor: alpha(theme.palette.common.black, 0.5),
        },
        // Column header
        ".rbc-time-header-content": {
          flexBasis: `calc((100vw - 88px) / 3 + 1px)`,
          borderLeftColor: alpha(theme.palette.common.black, 0.5),
          ".rbc-header": {
            padding: 0,
            borderBottom: "none",
          },
          ".rbc-row.rbc-row-resource": {
            borderBottom: "none",
          },
          ".rbc-allday-cell": {
            display: "none",
          },
        },
      },
      // Below content
      ".rbc-time-content": {
        borderTopWidth: 1,
        borderTopColor: alpha(theme.palette.common.black, 0.5),
        // Timeline
        ".rbc-time-gutter": {
          width: 88,
          borderRightColor: alpha(theme.palette.common.black, 0.5),
          ".rbc-timeslot-group": {
            backgroundColor: "#DCDFE0",
            ".rbc-time-slot": {
              fontSize: theme.typography.body2.fontSize,
              fontWeight: 500,
              textAlign: "left",
            },
            // Move anything other than 00:00 onto the line
            "&:not(:first-of-type)": {
              ".rbc-time-slot": {
                ".rbc-label": {
                  display: "block",
                  marginTop: "-1.5em",
                },
              },
            },
            // Add 24:00
            "&:last-of-type": {
              ".MuiBox-root:last-child": {
                ".rbc-time-slot::before": {
                  position: "absolute",
                  left: 5,
                  bottom: 0,
                  content: "'24:00'",
                },
              },
            },
          },
        },
        // Column(AWV)
        ".rbc-day-slot": {
          minWidth: `calc((100vw - 88px) / 3)`,
          // Task card container
          ".rbc-events-container": {
            marginLeft: theme.spacing(3),
            marginRight: theme.spacing(3),
          },
          // Current time line
          ".rbc-current-time-indicator": {
            left: -88,
            height: 3,
            backgroundColor: lighten(theme.palette.primary.main, 0.25),
            border: "none",
            zIndex: 10, // to be greater than or equal to .rbc-time-header-gutter(defs in time-column.scss)
          },
          // Task card
          ".rbc-event": {
            padding: 0,
            border: "none",
            backgroundColor: "transparent",
            overflow: "visible",
          },
        },
        // Slot group (1hour)
        ".rbc-timeslot-group": {
          backgroundColor: "#E6E9EB",
          borderLeftColor: alpha(theme.palette.common.black, 0.5),
          borderBottomColor: alpha(theme.palette.common.black, 0.3),
          ".rbc-time-slot": {
            borderTop: "none",
          },
        },
      },
    },
  },
}));
